import React, { useState } from 'react';
import { AssessmentPriority, AssessmentResultItem } from '../../types';

/**
 * امتیاز هر بخش روی بازهٔ ۰ تا ۲۵ است و **کمتر یعنی اولویت بالاتر**.
 *
 * رنگ‌ها از پالت وضعیت (status) گرفته شده‌اند نه از پالت دسته‌ای، چون رنگ اینجا
 * معنای «چقدر فوری است» را حمل می‌کند نه هویت یک سری داده را. دو رنگ کهربایی و
 * سبز روی زمینهٔ سفید کنتراست کمتر از ۳:۱ دارند، بنابراین طبق قاعدهٔ relief
 * هر ردیف علاوه بر رنگ، برچسب متنی اولویت و عدد امتیاز را هم دارد؛ یعنی رنگ
 * هیچ‌وقت تنها حامل معنا نیست.
 */
export const PRIORITY_COLORS: Record<AssessmentPriority, string> = {
  HIGH: '#d03b3b',
  MEDIUM: '#fab219',
  LOW: '#0ca30c',
};

export const PRIORITY_LABELS: Record<AssessmentPriority, string> = {
  HIGH: 'اولویت بالا',
  MEDIUM: 'اولویت متوسط',
  LOW: 'اولویت پایین',
};

/** پس‌زمینهٔ ملایم چیپ برچسب — رنگ متن همیشه از توکن‌های متنی می‌آید */
const PRIORITY_CHIP: Record<AssessmentPriority, string> = {
  HIGH: 'border-[#F3C9C9] bg-[#FDF2F2] text-[#8E2020]',
  MEDIUM: 'border-[#F4DFAE] bg-[#FEF8EA] text-[#7A5405]',
  LOW: 'border-[#BFE6BF] bg-[#F1FAF1] text-[#0A6B0A]',
};

const MAX_SCORE = 25;
const fa = (n: number) => n.toLocaleString('fa-IR');

export const PriorityChip: React.FC<{ priority: AssessmentPriority; className?: string }> = ({
  priority,
  className = '',
}) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black ${PRIORITY_CHIP[priority]} ${className}`}
  >
    <span
      aria-hidden
      className="h-2 w-2 rounded-full"
      style={{ backgroundColor: PRIORITY_COLORS[priority] }}
    />
    {PRIORITY_LABELS[priority]}
  </span>
);

// ─── نمودار میله‌ای رتبه‌بندی‌شده ─────────────────────────────────────────────

interface PriorityBarsProps {
  items: AssessmentResultItem[];
  /** محدود کردن تعداد ردیف‌ها؛ خالی = همه */
  limit?: number;
  onSelect?: (item: AssessmentResultItem) => void;
}

/**
 * میله‌های افقی مرتب از فوری‌ترین به کم‌اهمیت‌ترین.
 * با HTML/CSS ساخته شده نه SVG، تا راست‌چین بودن و شکستن خط عنوان‌های فارسی
 * به‌صورت طبیعی کار کند و روی موبایل هم بدون مقیاس‌بندی دستی درست بیفتد.
 */
export const PriorityBars: React.FC<PriorityBarsProps> = ({ items, limit, onSelect }) => {
  const rows = limit ? items.slice(0, limit) : items;
  const [hovered, setHovered] = useState<string | null>(null);

  if (!rows.length) return null;

  return (
    <div className="space-y-2">
      {rows.map((item) => {
        const pct = Math.max(4, (item.score25 / MAX_SCORE) * 100);
        const color = PRIORITY_COLORS[item.priority];
        const isHovered = hovered === item.sectionId;
        const interactive = !!onSelect;

        return (
          <div
            key={item.sectionId}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={interactive ? () => onSelect!(item) : undefined}
            onKeyDown={
              interactive
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect!(item);
                    }
                  }
                : undefined
            }
            onMouseEnter={() => setHovered(item.sectionId)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(item.sectionId)}
            onBlur={() => setHovered(null)}
            className={`rounded-2xl border px-3 py-2.5 transition ${
              isHovered ? 'border-[#C8D6FF] bg-[#FAFCFF]' : 'border-[#E6ECFA] bg-white'
            } ${interactive ? 'cursor-pointer' : ''}`}
          >
            <div className="flex items-center gap-2.5">
              <span className="w-6 shrink-0 text-center text-[11px] font-black text-slate-400">
                {fa(item.rank)}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-slate-800">
                {item.title}
              </span>
              <PriorityChip priority={item.priority} className="hidden sm:inline-flex" />
              <span className="shrink-0 text-[13px] font-black tabular-nums text-slate-900">
                {fa(item.score25)}
                <span className="text-[10px] font-bold text-slate-400"> / {fa(MAX_SCORE)}</span>
              </span>
            </div>

            <div className="mt-2 flex items-center gap-2">
              <div
                className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#EEF2F7]"
                role="img"
                aria-label={`${item.title}: امتیاز ${fa(item.score25)} از ${fa(MAX_SCORE)} — ${PRIORITY_LABELS[item.priority]}`}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-700 ease-out"
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </div>
              <PriorityChip priority={item.priority} className="sm:hidden" />
            </div>

            {item.course ? (
              <p className="mt-1.5 truncate text-[11px] font-bold text-[#1C3B9B]">
                دورهٔ پیشنهادی: {item.course.title}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

// ─── خلاصهٔ عددی ─────────────────────────────────────────────────────────────

export const AssessmentSummaryTiles: React.FC<{
  sectionCount: number;
  highPriorityCount: number;
  averageScore25: number;
}> = ({ sectionCount, highPriorityCount, averageScore25 }) => (
  <div className="grid grid-cols-3 gap-2.5">
    <div className="rounded-2xl border border-[#E6ECFA] bg-white px-3 py-3 text-center">
      <p className="text-[10px] font-bold text-slate-500">بخش‌های بررسی‌شده</p>
      <p className="mt-1 text-lg font-black text-slate-900">{fa(sectionCount)}</p>
    </div>
    <div className="rounded-2xl border border-[#F3C9C9] bg-[#FDF2F2] px-3 py-3 text-center">
      <p className="text-[10px] font-bold text-[#8E2020]">اولویت بالا</p>
      <p className="mt-1 text-lg font-black text-[#8E2020]">{fa(highPriorityCount)}</p>
    </div>
    <div className="rounded-2xl border border-[#E6ECFA] bg-white px-3 py-3 text-center">
      <p className="text-[10px] font-bold text-slate-500">میانگین امتیاز</p>
      <p className="mt-1 text-lg font-black text-slate-900">
        {fa(averageScore25)}
        <span className="text-[10px] font-bold text-slate-400"> / {fa(MAX_SCORE)}</span>
      </p>
    </div>
  </div>
);
